import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { borderFor, controlBase } from './Field';
import { useEscape } from './Layers';
import { IconCheck, IconChevronDown } from './icons';

/**
 * Listbox: a single-choice select whose menu is drawn by us, not the OS.
 *
 * Use it where the native `Select` popup would break the page's look — any
 * dialog or form a customer sees — or where options need an icon or a second
 * line (a contact's address, a plan's price). Keep `Select` for dense
 * toolbar filters where the native control is fine, and `Combobox` when the
 * list is long enough that people need to type to find an option.
 *
 * - The trigger is a real `<button>`, so `Field`'s label and a dialog's
 *   `data-autofocus` work unchanged.
 * - WAI-ARIA "select-only combobox": `role=combobox` + `aria-haspopup=listbox`
 *   on the trigger, `aria-activedescendant` names the highlighted option.
 *   Focus stays on the trigger while the list is open.
 * - Keyboard: ↓ ↑ Enter Space open; ↓ ↑ move, Home/End jump, Enter/Space
 *   pick, Esc closes without changing (and is consumed before an enclosing
 *   Dialog sees it), Tab closes and moves on. Typing letters jumps to the
 *   first matching option, open or closed.
 * - Opens below; flips above the trigger when the viewport is short.
 * - The list is a floating layer (`shadow-v2-float`), the only elevation.
 */
export interface ListboxOption<V extends string = string> {
  value: V;
  label: React.ReactNode;
  /** Quieter second line in the list; inline after the label on the trigger. */
  description?: React.ReactNode;
  /** 14px icon in front of the label. */
  leading?: React.ReactNode;
  disabled?: boolean;
  /** Hairline above this option — for a trailing action like "Add new…". */
  separatorBefore?: boolean;
  /** Plain text for typeahead; needed only when `label` is not a string. */
  text?: string;
  /** Shown on the trigger in place of label and description, for a narrow
   *  control such as a phone number's country code. */
  short?: React.ReactNode;
}

export interface ListboxProps<V extends string = string>
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'value' | 'onChange' | 'children'
  > {
  value: V | '';
  onChange: (value: V) => void;
  options: ListboxOption<V>[];
  placeholder?: string;
  invalid?: boolean;
  size?: 'sm' | 'md';
  /** Shown as a single quiet row when `options` is empty. */
  emptyText?: string;
}

const LIST_MAX_HEIGHT = 280;
const TYPEAHEAD_RESET_MS = 500;

function textOf<V extends string>(o: ListboxOption<V>): string {
  if (o.text) return o.text;
  return typeof o.label === 'string' || typeof o.label === 'number'
    ? String(o.label)
    : '';
}

export function Listbox<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  invalid,
  size = 'md',
  emptyText = 'Nothing to choose from.',
  className,
  disabled,
  onKeyDown,
  onClick,
  onBlur,
  ...rest
}: ListboxProps<V>) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [above, setAbove] = useState(false);
  const typed = useRef('');
  const typedTimer = useRef<number>();

  const selectedIndex = options.findIndex((o) => o.value === value);
  const selected = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  const enabledIndexes = options
    .map((o, i) => (o.disabled ? -1 : i))
    .filter((i) => i >= 0);
  const firstEnabled = enabledIndexes[0] ?? -1;
  const lastEnabled = enabledIndexes[enabledIndexes.length - 1] ?? -1;

  const openList = useCallback(() => {
    if (disabled) return;
    typed.current = '';
    setActive(
      selectedIndex >= 0 && !options[selectedIndex].disabled
        ? selectedIndex
        : firstEnabled,
    );
    setOpen(true);
  }, [disabled, selectedIndex, options, firstEnabled]);

  const close = useCallback(() => {
    setOpen(false);
    setActive(-1);
  }, []);

  useEscape(open, close);

  const pick = (i: number) => {
    const o = options[i];
    if (!o || o.disabled) return;
    close();
    if (o.value !== value) onChange(o.value);
  };

  const step = (from: number, dir: 1 | -1) => {
    if (enabledIndexes.length === 0) return -1;
    const pos = enabledIndexes.indexOf(from);
    if (pos === -1) return dir === 1 ? firstEnabled : lastEnabled;
    return enabledIndexes[
      (pos + dir + enabledIndexes.length) % enabledIndexes.length
    ];
  };

  // Letters typed within half a second form a prefix; repeating one letter
  // cycles through the options that start with it.
  const typeahead = (key: string) => {
    window.clearTimeout(typedTimer.current);
    const k = key.toLowerCase();
    const repeat =
      typed.current.length > 0 && typed.current.split('').every((c) => c === k);
    typed.current = repeat ? k : typed.current + k;
    typedTimer.current = window.setTimeout(
      () => (typed.current = ''),
      TYPEAHEAD_RESET_MS,
    );
    const prefix = typed.current;
    const start = open ? active : selectedIndex;
    const order = [
      ...enabledIndexes.filter((i) => i > start),
      ...enabledIndexes.filter((i) => i <= start),
    ];
    const hit = order.find((i) =>
      textOf(options[i]).toLowerCase().startsWith(prefix),
    );
    if (hit === undefined) return;
    if (open) setActive(hit);
    else pick(hit);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  // Flip above the trigger when there is not enough room below it.
  useLayoutEffect(() => {
    if (!open || !rootRef.current) return;
    const r = rootRef.current.getBoundingClientRect();
    const need =
      Math.min(
        LIST_MAX_HEIGHT,
        listRef.current?.scrollHeight ?? LIST_MAX_HEIGHT,
      ) + 8;
    const below = window.innerHeight - r.bottom;
    setAbove(below < need && r.top > below);
  }, [open]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${active}"]`)
      ?.scrollIntoView?.({ block: 'nearest' });
  }, [open, active]);

  useEffect(() => () => window.clearTimeout(typedTimer.current), []);

  const isTypeaheadKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 &&
    e.key !== ' ' &&
    !e.metaKey &&
    !e.ctrlKey &&
    !e.altKey;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented || disabled) return;
    const { key } = e;
    if (!open) {
      if (
        key === 'ArrowDown' ||
        key === 'ArrowUp' ||
        key === 'Enter' ||
        key === ' '
      ) {
        e.preventDefault();
        openList();
      } else if (isTypeaheadKey(e)) {
        e.preventDefault();
        typeahead(key);
      }
      return;
    }
    switch (key) {
      case 'ArrowDown':
        e.preventDefault();
        setActive((i) => step(i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActive((i) => step(i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActive(firstEnabled);
        break;
      case 'End':
        e.preventDefault();
        setActive(lastEnabled);
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (active >= 0) pick(active);
        else close();
        break;
      case 'Tab':
        close();
        break;
      default:
        if (isTypeaheadKey(e)) {
          e.preventDefault();
          typeahead(key);
        }
    }
  };

  const activeId = open && active >= 0 ? `${listId}-${active}` : undefined;

  return (
    <div ref={rootRef} className={clsx('relative', className)}>
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        aria-invalid={invalid || undefined}
        disabled={disabled}
        onClick={(e) => {
          onClick?.(e);
          if (e.defaultPrevented) return;
          if (open) close();
          else openList();
        }}
        onKeyDown={handleKeyDown}
        onBlur={(e) => {
          onBlur?.(e);
          if (!rootRef.current?.contains(e.relatedTarget as Node | null)) {
            close();
          }
        }}
        className={clsx(
          controlBase,
          borderFor(invalid),
          size === 'sm' ? 'h-7 text-v2-12' : 'h-8',
          'flex items-center gap-2 pl-2.5 pr-8 text-left',
          open && !invalid && 'border-v2-accent ring-2 ring-v2-accent/25',
        )}
        {...rest}
      >
        {selected ? (
          <>
            {selected.leading && (
              <span className="shrink-0 text-v2-fg-3">{selected.leading}</span>
            )}
            <span className="min-w-0 flex-1 truncate">
              {selected.short ?? (
                <>
                  {selected.label}
                  {selected.description && (
                    <span className="text-v2-fg-3">
                      {' '}
                      · {selected.description}
                    </span>
                  )}
                </>
              )}
            </span>
          </>
        ) : (
          <span className="min-w-0 flex-1 truncate text-v2-fg-4">
            {placeholder}
          </span>
        )}
        <IconChevronDown
          className={clsx(
            'pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-v2-fg-3 transition-transform duration-100',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <ul
          ref={listRef}
          id={listId}
          role="listbox"
          aria-labelledby={rest.id}
          tabIndex={-1}
          style={{ maxHeight: LIST_MAX_HEIGHT }}
          // Keeps focus on the trigger so choosing an option doesn't blur it.
          onMouseDown={(e) => e.preventDefault()}
          className={clsx(
            'v2-fade-in absolute left-0 z-50 w-full min-w-[180px] overflow-y-auto rounded-v2-lg border border-v2-line bg-v2-surface p-1 shadow-v2-float',
            above ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
        >
          {options.length === 0 && (
            <li className="px-2 py-2 text-v2-12 text-v2-fg-3">{emptyText}</li>
          )}
          {options.map((o, i) => {
            const isActive = i === active;
            const isSelected = o.value === value;
            return (
              <React.Fragment key={o.value}>
                {o.separatorBefore && i > 0 && (
                  <li role="separator" className="my-1 h-px bg-v2-line" />
                )}
                <li
                  id={`${listId}-${i}`}
                  data-index={i}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={o.disabled || undefined}
                  onMouseEnter={() => !o.disabled && setActive(i)}
                  onClick={() => pick(i)}
                  className={clsx(
                    'flex items-center gap-2 rounded-v2 px-2 py-1.5 text-v2-13 transition-colors duration-75',
                    o.disabled
                      ? 'cursor-not-allowed text-v2-fg-4'
                      : 'cursor-pointer',
                    !o.disabled && (isSelected ? 'text-v2-fg' : 'text-v2-fg-2'),
                    isActive && !o.disabled && 'bg-v2-sunken text-v2-fg',
                  )}
                >
                  {o.leading && (
                    <span
                      className={clsx(
                        'shrink-0',
                        o.disabled ? 'text-v2-fg-4' : 'text-v2-fg-3',
                      )}
                    >
                      {o.leading}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span
                      className={clsx(
                        'block truncate',
                        isSelected && 'font-medium',
                      )}
                    >
                      {o.label}
                    </span>
                    {o.description && (
                      <span className="block truncate text-v2-12 text-v2-fg-3">
                        {o.description}
                      </span>
                    )}
                  </span>
                  {isSelected && (
                    <IconCheck size={14} className="shrink-0 text-v2-accent" />
                  )}
                </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
}
